import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast, Toaster } from 'sonner';
import { Save, Loader2, ShieldCheck, TriangleAlert } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const SecretAdmin = () => {
  const { token } = useParams();

  const [stripeLink, setStripeLink] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings`);
      setStripeLink(data.stripe_link || '');
      setButtonText(data.button_text || '');
    } catch (e) {
      toast.error("Impossible de charger les paramètres (base de données indisponible).");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        { stripe_link: stripeLink, button_text: buttonText },
        { headers: { 'X-Admin-Token': token } }
      );
      toast.success('Modifications enregistrées ✅');
      setInvalid(false);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setInvalid(true);
        toast.error('Lien secret invalide.');
      } else {
        toast.error("Erreur lors de l'enregistrement (base de données indisponible).");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <Toaster position="top-center" richColors />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <ShieldCheck className="w-6 h-6 text-[#01C38D]" />
          <h1 className="text-2xl font-bold text-gray-900">Paramètres du site</h1>
        </div>

        {invalid && (
          <div
            className="mb-6 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            data-testid="secret-invalid-banner"
          >
            <TriangleAlert className="w-4 h-4" />
            Lien secret invalide. Vérifiez l'adresse exacte de votre lien d'administration.
          </div>
        )}

        <Card className="border-[#01C38D]/20 shadow-sm">
          <CardHeader>
            <CardTitle>Bouton de paiement</CardTitle>
            <CardDescription>
              Modifiez le lien Stripe et le texte du bouton principal. Aucune connexion requise —
              cet accès est protégé par votre lien secret.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500 py-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
              </div>
            ) : (
              <form onSubmit={handleSave} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="stripe">Lien de paiement Stripe</Label>
                  <Input
                    id="stripe"
                    type="url"
                    value={stripeLink}
                    onChange={(e) => setStripeLink(e.target.value)}
                    required
                    placeholder="https://buy.stripe.com/..."
                    data-testid="secret-stripe-input"
                  />
                  <p className="text-xs text-gray-500">L'adresse du lien de paiement Stripe à ouvrir.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="btntext">Texte du bouton</Label>
                  <Textarea
                    id="btntext"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    required
                    rows={2}
                    placeholder="Ex: Acheter maintenant"
                    data-testid="secret-buttontext-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#01C38D] hover:bg-[#00A878] text-white gap-2"
                  data-testid="secret-save-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecretAdmin;
